import { useCallback, useEffect, useRef, useState } from 'react';
import type { DeviceLicenseSummary, DeviceSummary } from '@aiscoreboard/contracts';
import { cameraAccess, listCameraDevices, logout, updateActiveDevice, type CameraAccess } from '../lib/accounts';
import { controlToken, OPERATOR_EVENT, OPERATOR_KEY, operatorToken, saveOperatorToken } from '../lib/operator';
import { SEAT_TOKEN_KEYS, SESSION_EXPIRY_EVENT } from '../lib/sessionTimeout';
import { SignInPanel } from '../components/SignInPanel';
import { isCameraAvailable } from '../lib/camera';
import { useT, type MessageKey } from '../lib/i18n';
import { pullToRefreshMessage, usePullToRefresh } from '../lib/usePullToRefresh';
import { CameraYouTubeAuthorization } from '../components/CameraYouTubeAuthorization';
import { youtubeDevicePath, youtubeRequest } from '../lib/youtube';
import {
  DEVICE_PAIRING_EVENT, PREFERRED_DEVICE_KEY, devicePairingToken, forgetDevicePairing, preferredPairing, saveDevicePairing, selectCamera,
} from '../lib/devicePairing';

function timeAgo(iso: string | null, t: (key: MessageKey, vars?: Record<string, string | number>) => string): string {
  if (!iso) return t('camera.timeNever');
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return t('camera.timeJustNow');
  if (secs < 3600) return t('camera.timeMinutesAgo', { n: Math.floor(secs / 60) });
  if (secs < 86400) return t('camera.timeHoursAgo', { n: Math.floor(secs / 3600) });
  return t('camera.timeDaysAgo', { n: Math.floor(secs / 86400) });
}

export function CameraScreen({ onBack }: { onBack: () => void }) {
  const t = useT();
  const [accountToken, setAccountToken] = useState(() => operatorToken() ?? controlToken());
  const [signingIn, setSigningIn] = useState(false);
  const [access, setAccess] = useState<CameraAccess | null>(null);
  const [devices, setDevices] = useState<DeviceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<DeviceSummary | null>(null);
  const [rtmpUrl, setRtmpUrl] = useState('');
  const [streamKey, setStreamKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [licenseError, setLicenseError] = useState<string | null>(null);
  const [settingsVersion, setSettingsVersion] = useState(0);
  const [bindingBusy, setBindingBusy] = useState(false);
  const callbackDevice = useRef(new URLSearchParams(window.location.search).get('device'));
  const completion = useRef<Promise<{ token: string; device: DeviceSummary }> | null>(null);
  const loadGeneration = useRef(0);
  useEffect(() => {
    const update = (event: Event): void => {
      if (event instanceof StorageEvent && event.key !== null
        && event.key !== OPERATOR_KEY && event.key !== `${OPERATOR_KEY}.expiry`
        && !SEAT_TOKEN_KEYS.some(key => event.key === key || event.key === `${key}.expiry`)) return;
      const next = operatorToken() ?? (event.type === OPERATOR_EVENT ? null
        : accountToken === controlToken() ? accountToken : null);
      if (next === accountToken) return;
      loadGeneration.current += 1;
      setAccountToken(next);
      setSelected(null);
      setDevices([]);
      setAccess(null);
      setStreamKey('');
    };
    window.addEventListener(OPERATOR_EVENT, update);
    window.addEventListener('storage', update);
    window.addEventListener(SESSION_EXPIRY_EVENT, update);
    return () => {
      window.removeEventListener(OPERATOR_EVENT, update);
      window.removeEventListener('storage', update);
      window.removeEventListener(SESSION_EXPIRY_EVENT, update);
    };
  }, [accountToken]);
  const [tabletBinding, setTabletBinding] = useState(() => ({
    deviceId: localStorage.getItem(PREFERRED_DEVICE_KEY), pairing: preferredPairing(),
  }));
  const canConfigure = Boolean(selected && access?.venues.some(v => v.id === selected.venueId && v.canConfigure));
  const isTabletBound = (device: DeviceSummary): boolean => tabletBinding.deviceId === device.id
    && tabletBinding.pairing?.deviceId === device.id;

  const load = useCallback(async (): Promise<void> => {
    const generation = ++loadGeneration.current;
    setLoading(true);
    setError(null);
    if (!accountToken) {
      setDevices([]);
      setSelected(null);
      setAccess(null);
      setLoading(false);
      return;
    }
    try {
      const permissions = await cameraAccess(accountToken);
      if (generation !== loadGeneration.current) return;
      setAccess(permissions);
      const returnId = callbackDevice.current;
      if (returnId && new URLSearchParams(window.location.search).get('youtube') === 'connected'
        && !devicePairingToken(returnId)) {
        completion.current ??= youtubeRequest(accountToken, `${youtubeDevicePath(returnId)}/complete`, 'POST', {});
        try {
          const paired = await completion.current;
          if (generation !== loadGeneration.current) return;
          saveDevicePairing(returnId, paired.token);
        } catch (err) {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
      const result = await listCameraDevices(undefined, accountToken);
      if (generation !== loadGeneration.current) return;
      setDevices(result.devices);
      if (returnId) {
        const returned = result.devices.find(device => device.id === returnId);
        if (returned) {
          setSelected(returned);
          setRtmpUrl(returned.rtmpUrl ?? '');
        }
        callbackDevice.current = null;
      }
    } catch (err) {
      if (generation !== loadGeneration.current) return;
      setDevices([]);
      setSelected(null);
      setAccess(null);
      setError(err instanceof Error ? err.message : t('camera.loadFailed'));
    } finally {
      if (generation === loadGeneration.current) setLoading(false);
    }
  }, [accountToken, t]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    setStreamKey('');
    setRtmpUrl('');
    setSaveError(null);
    return () => { loadGeneration.current += 1; };
  }, [accountToken]);
  useEffect(() => {
    const update = () => setTabletBinding({
      deviceId: localStorage.getItem(PREFERRED_DEVICE_KEY), pairing: preferredPairing(),
    });
    window.addEventListener(DEVICE_PAIRING_EVENT, update);
    window.addEventListener('storage', update);
    return () => {
      window.removeEventListener(DEVICE_PAIRING_EVENT, update);
      window.removeEventListener('storage', update);
    };
  }, []);

  const deviceChanged = useCallback((updated: DeviceSummary): void => {
    setSelected(previous => previous?.id === updated.id ? updated : previous);
    setDevices(previous => previous.map(device => device.id === updated.id ? updated : device));
  }, []);
  useEffect(() => {
    const id = selected?.id;
    setLicenseError(null);
    if (!id) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const poll = async (): Promise<void> => {
      try {
        const license = await youtubeRequest<DeviceLicenseSummary>(accountToken ?? '', `/api/devices/${encodeURIComponent(id)}/license`,
          'GET', undefined, controller.signal);
        if (!controller.signal.aborted) {
          setSelected(previous => previous?.id === id ? { ...previous, license } : previous);
          setLicenseError(null);
        }
      } catch (error) {
        if (!controller.signal.aborted) setLicenseError(error instanceof Error ? error.message : String(error));
      } finally {
        if (!controller.signal.aborted) timer = setTimeout(() => void poll(), 30_000);
      }
    };
    void poll();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [selected?.id, accountToken]);
  const refresh = usePullToRefresh({ onRefresh: load });
  const refreshMessage = pullToRefreshMessage(refresh.pullDistance, refresh.refreshing);

  const select = (device: DeviceSummary): void => {
    setSelected(device);
    setRtmpUrl(device.rtmpUrl ?? '');
    setStreamKey('');
    setSaveError(null);
  };

  const setTabletCameraBinding = async (enabled: boolean): Promise<void> => {
    if (!selected || !accountToken || !canConfigure || bindingBusy) return;
    setBindingBusy(true);
    setSaveError(null);
    try {
      if (!enabled) {
        const token = devicePairingToken(selected.id);
        if (token) {
          await youtubeRequest(token, `${youtubeDevicePath(selected.id)}/paired`, 'DELETE', {});
          forgetDevicePairing(selected.id);
        } else selectCamera(null);
        const url = new URL(window.location.href);
        if (url.searchParams.get('device') === selected.id && url.searchParams.get('youtube') === 'connected') {
          url.searchParams.set('youtube', 'ready');
          window.history.replaceState(null, '', url);
        }
        return;
      }
      const paired = await youtubeRequest<{ token: string }>(accountToken,
        `${youtubeDevicePath(selected.id)}/pair`, 'POST', {});
      saveDevicePairing(selected.id, paired.token);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setBindingBusy(false);
    }
  };

  const save = async (): Promise<void> => {
    if (!selected || !accountToken || !canConfigure || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await updateActiveDevice(selected.id, {
        rtmpUrl: rtmpUrl || null, streamKey: streamKey || undefined,
      }, accountToken);
      deviceChanged(updated);
      setStreamKey('');
      const url = new URL(window.location.href);
      if (url.searchParams.has('youtube')) {
        url.searchParams.set('youtube', 'ready');
        window.history.replaceState(null, '', url);
      }
      setSettingsVersion(version => version + 1);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : t('camera.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="relative flex h-full w-full flex-col overflow-y-auto"
      onTouchStart={refresh.onTouchStart} onTouchMove={refresh.onTouchMove} onTouchEnd={refresh.onTouchEnd}>
      <p className="pointer-events-none absolute left-0 right-0 top-1 z-10 text-center text-xs text-white/50 transition-opacity"
        style={{ opacity: refresh.pullDistance > 0 || refresh.refreshing ? 1 : 0 }}>
        {refreshMessage === 'refreshing' ? t('common.loading')
          : refreshMessage === 'release' ? t('common.releaseToRefresh') : t('common.pullToRefresh')}
      </p>
      <header className="flex shrink-0 items-center gap-4 border-b border-white/10 px-5 py-4 max-sm:grid max-sm:grid-cols-[auto_1fr_auto_auto]">
        <button type="button" onClick={selected ? () => setSelected(null) : onBack}
          className="min-h-9 text-sm text-white/50 hover:text-white/80 max-sm:col-start-1 max-sm:row-start-1">← {t('common.back')}</button>
        <h1 className="min-w-0 flex-1 break-words text-lg font-bold text-white max-sm:col-span-3 max-sm:col-start-2 max-sm:row-start-1">{selected ? selected.name : t('camera.title')}</h1>
        <button type="button" onClick={() => setSigningIn(true)} className="min-h-9 text-xs text-white/60 max-sm:col-span-2 max-sm:col-start-1 max-sm:row-start-2">{t('common.signIn')}</button>
        {operatorToken() && <button type="button" className="min-h-9 text-xs text-white/60 max-sm:col-start-3 max-sm:row-start-2" onClick={() => {
          const token = operatorToken();
          if (token) void logout(token).catch(() => undefined);
          saveOperatorToken(null);
          setAccountToken(null);
          setSelected(null);
          setDevices([]);
          setAccess(null);
        }}>{t('common.signOut')}</button>}
        {!selected && <button type="button" onClick={() => void load()} className="min-h-9 min-w-9 text-xs text-white/30 hover:text-white/60 max-sm:col-start-4 max-sm:row-start-2">↻</button>}
      </header>
      <div className="mx-auto w-full max-w-xl flex-1 px-5 py-6">
        {!accountToken && <p role="status" className="mb-4 text-sm text-white/70">{t('camera.loginRequired')}</p>}
        {accountToken && access && !access.venues.some(v => v.canConfigure) &&
          <p role="status" className="mb-4 text-sm text-white/70">{t('camera.adminRequired')}</p>}
        {access?.platformAdmin && <p className="mb-4 text-sm text-amber-300">{t('camera.platformAdmin')}</p>}
        {error && <p role="alert" className="mb-3 text-sm text-red-400">{error}</p>}
        {!selected ? <>
          {loading && <p className="text-sm text-white/30">{t('camera.looking')}</p>}
          {!loading && accountToken && access && devices.length === 0 && <div className="rounded-lg border border-white/10 p-6 text-center text-sm text-white/40">
            <p className="text-2xl mb-2">📷</p>
            <p>{t('camera.noneAssigned')}</p>
            <p className="mt-1 text-xs text-white/25">{t('camera.noneAssignedHint')}</p>
          </div>}
          <div className="flex flex-col gap-3">
            {devices.map(device => <button key={device.id} type="button" onClick={() => select(device)}
              className="panel group flex items-center gap-4 px-5 py-4 text-left hover:brightness-125 transition">
              <span className="relative text-2xl">📷
                <span className={`absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full ring-2 ring-black ${
                  isCameraAvailable(device) ? 'bg-green-400' : 'bg-white/30'}`} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-white">{device.name}</p>
                <p className="text-xs text-white/40 max-sm:min-h-[2rem]">
                  {isTabletBound(device) ? t('camera.boundToTablet') : t('camera.notBoundToTablet')}
                  {' · '}{t('camera.lastSeen')} {timeAgo(device.lastSeenAt, t)}
                </p>
              </div>
              <span className="text-white/30 group-hover:text-white/60">→</span>
            </button>)}
          </div>
        </> : <div className="panel flex flex-col gap-5 p-5">
          {canConfigure && <label className="flex cursor-pointer items-center gap-3">
            <input type="checkbox" checked={isTabletBound(selected)}
              onChange={event => void setTabletCameraBinding(event.target.checked)} disabled={bindingBusy}
              className="h-4 w-4 accent-green-500" />
            <span className="text-sm text-white max-sm:min-h-[2.5rem]">{t('camera.bindTabletToCamera')}</span>
          </label>}
          <p className="text-xs text-white/50 max-sm:min-h-[2rem]">{t('camera.matchControlsStreaming')}</p>
          {selected.license && <section className="space-y-1 rounded border border-white/15 p-3 text-sm">
            <h2 className="font-bold">{t('license.title')}</h2>
            <p role="status">{t('license.remaining', { n: selected.license.remainingDays })}</p>
            {['unlicensed', 'expired', 'revoked'].includes(selected.license.status) &&
              <p className="text-amber-300">{t(`license.status.${selected.license.status}`)}</p>}
            <p className="text-xs text-white/60 max-sm:min-h-[2rem]">{t('license.renew')} <a
              href="mailto:shangyueqi@gmail.com" className="text-sky-300 underline">shangyueqi@gmail.com</a></p>
            {!selected.license.enforcementEnabled && <p className="text-amber-300">{t('license.migration')}</p>}
          </section>}
          {licenseError && <p role="alert" className="text-sm text-red-400">{t('license.title')}: {licenseError}</p>}
          {canConfigure && accountToken && <CameraYouTubeAuthorization key={`${selected.id}:${settingsVersion}`}
            device={selected} accountToken={accountToken} onChanged={deviceChanged} />}
          {canConfigure && <p className="text-xs text-white/50 max-sm:min-h-[3rem]">{t('youtube.unpairHint')}</p>}
          {saveError && <p role="alert" className="text-xs text-red-400">{saveError}</p>}
          {canConfigure && <>
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-white/50">{t('camera.rtmpUrl')}</label>
              <input type="text" value={rtmpUrl} onChange={event => setRtmpUrl(event.target.value)}
                placeholder="rtmp://a.rtmp.youtube.com/live2"
                className="w-full rounded bg-black/40 px-3 py-2 text-sm text-white ring-1 ring-white/10 focus:ring-white/30" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-white/50">
                {t('camera.streamKey')} {selected.hasStreamKey &&
                  <span className="text-green-400">({t('camera.keyStored')} — ••••{selected.streamKeyHint})</span>}
              </label>
              <input type="password" value={streamKey} onChange={event => setStreamKey(event.target.value)}
                placeholder={selected.hasStreamKey ? t('camera.keepCurrentKey') : t('camera.pasteStreamKey')}
                className="w-full rounded bg-black/40 px-3 py-2 text-sm text-white ring-1 ring-white/10 focus:ring-white/30" />
            </div>
            <button type="button" disabled={saving} onClick={() => void save()}
              className="w-full rounded bg-white/10 py-2 text-sm font-bold text-white hover:bg-white/15 disabled:opacity-40">
              {saving ? t('camera.saving') : t('camera.save')}
            </button>
          </>}
        </div>}
      </div>
      {signingIn && <SignInPanel title={t('camera.title')} onCancel={() => setSigningIn(false)} onDone={auth => {
        saveOperatorToken(auth.token);
        setAccountToken(auth.token);
        setSelected(null);
        setDevices([]);
        setAccess(null);
        completion.current = null;
        setSigningIn(false);
      }} />}
    </main>
  );
}
